import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useRef, useState } from 'react';
import { FaYoutube } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { SiG2, SiTrustpilot } from 'react-icons/si';

import {
  DEFAULT_TESTIMONIAL_RATING,
  type Testimonial,
  TESTIMONIALS,
  type TestimonialSource,
  type VideoTestimonial,
} from '@/constants/testimonials';

const MASONRY_GAP = 24;
const INITIAL_TESTIMONIAL_COUNT = 12;

interface MasonryPosition {
  width: number;
  x: number;
  y: number;
}

interface MasonryLayout {
  height: number;
  positions: MasonryPosition[];
}

const getColumnCount = (width: number) => {
  if (width >= 1024) return 3;
  if (width >= 640) return 2;
  return 1;
};

const StarRating = ({
  rating = DEFAULT_TESTIMONIAL_RATING,
  compact = false,
}: {
  rating?: number;
  compact?: boolean;
}) => (
  <div
    className='flex gap-[1.6px] text-[#fbbf24]'
    aria-label={`${rating} out of 5 stars`}
  >
    {Array.from({ length: rating }, (_, index) => (
      <svg
        key={index}
        aria-hidden='true'
        viewBox='0 0 20 20'
        className={compact ? 'h-3 w-3 fill-current' : 'h-5 w-5 fill-current'}
      >
        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
      </svg>
    ))}
  </div>
);

const CapterraIcon = () => (
  <svg aria-hidden='true' viewBox='0 0 24 24' className='h-5 w-5'>
    <path fill='#ff9d28' d='M3 2h18L10 11z' />
    <path fill='#18a8c9' d='M3 2l7 9-7 11z' />
    <path fill='#ef5350' d='M10 11L21 2l-8 20z' />
  </svg>
);

const SourceIcon = ({
  source,
  sourceUrl,
}: {
  source?: TestimonialSource;
  sourceUrl?: string;
}) => {
  if (!source) return null;

  const icons: Record<TestimonialSource, React.ReactNode> = {
    capterra: <CapterraIcon />,
    g2: <SiG2 className='h-5 w-5 text-[#ff492c]' />,
    google: <FcGoogle className='h-5 w-5' />,
    trustpilot: <SiTrustpilot className='h-5 w-5 text-[#00b67a]' />,
    youtube: <FaYoutube className='h-6 w-6 text-[#ff0000]' />,
  };

  const icon = icons[source];

  if (!sourceUrl) {
    return (
      <span aria-label={source} title={source}>
        {icon}
      </span>
    );
  }

  return (
    <a
      href={sourceUrl}
      target='_blank'
      rel='nofollow noopener noreferrer'
      aria-label={`Read this review on ${source}`}
      title={source}
    >
      {icon}
    </a>
  );
};

const TextTestimonialCard = ({
  testimonial,
}: {
  testimonial: Extract<Testimonial, { type: 'text' }>;
}) => (
  <article className='box-border w-full rounded-2xl border-2 border-black bg-white p-4 font-["DM_Sans",system-ui,-apple-system,BlinkMacSystemFont,"Segoe_UI",sans-serif] text-base leading-6 text-[#374151] shadow-[6px_6px_0_0_#7d56ed]'>
    <div className='flex flex-col gap-2'>
      <header className='flex h-[42px] items-center gap-4'>
        <div className='relative h-[42px] w-[42px] flex-none overflow-hidden rounded-full bg-[#f3f4f6]'>
          <Image
            src={testimonial.avatarUrl}
            alt={`${testimonial.author} avatar`}
            fill
            sizes='42px'
            loading='lazy'
            decoding='async'
            className='object-cover'
          />
        </div>
        <p className='min-w-0 flex-1 truncate text-base font-medium leading-5'>
          {testimonial.author}
        </p>
        <SourceIcon
          source={testimonial.source}
          sourceUrl={testimonial.sourceUrl}
        />
      </header>

      <div className='my-1'>
        <StarRating rating={testimonial.rating} />
      </div>

      {testimonial.title && (
        <h3 className='text-base font-semibold leading-6 tracking-[-0.4px]'>
          {testimonial.title}
        </h3>
      )}

      <p className='whitespace-pre-line text-left text-base leading-6 tracking-[-0.4px] opacity-90'>
        {testimonial.quote}
      </p>
    </div>
  </article>
);

const VideoPlayer = ({ testimonial }: { testimonial: VideoTestimonial }) => (
  <video
    src={testimonial.videoUrl}
    poster={testimonial.poster}
    aria-label={`${testimonial.author} video testimonial`}
    autoPlay
    controls
    playsInline
    preload='none'
    className='absolute inset-0 h-full w-full object-contain'
  />
);

const VideoTestimonialCard = ({
  testimonial,
}: {
  testimonial: VideoTestimonial;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <article className='box-border w-full overflow-hidden rounded-2xl bg-white font-["DM_Sans",system-ui,-apple-system,BlinkMacSystemFont,"Segoe_UI",sans-serif] text-base leading-6 text-[#374151] shadow-[6px_6px_0_0_#7d56ed]'>
      <div className='relative aspect-video overflow-hidden bg-black'>
        {isPlaying ? (
          <VideoPlayer testimonial={testimonial} />
        ) : (
          <>
            <Image
              src={testimonial.poster}
              alt=''
              fill
              sizes='(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
              loading='lazy'
              decoding='async'
              className='object-cover'
            />
            <div className='absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/70 to-transparent' />
            <div className='absolute inset-0 flex flex-col justify-end px-4 pb-4'>
              <div className='mb-1'>
                <StarRating rating={testimonial.rating} compact />
              </div>
              <div className='flex items-end gap-2 text-white'>
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-xl leading-7'>
                    {testimonial.author}
                  </p>
                  {testimonial.title && (
                    <p className='truncate text-sm leading-5 opacity-80'>
                      {testimonial.title}
                    </p>
                  )}
                </div>
                <button
                  type='button'
                  aria-label='Play'
                  title={`Play ${testimonial.author}'s testimonial`}
                  onClick={() => setIsPlaying(true)}
                  className='rounded p-1 text-white transition-colors duration-200 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
                >
                  <svg
                    aria-hidden='true'
                    viewBox='0 0 24 24'
                    className='h-6 w-6 fill-current'
                  >
                    <path d='M5 3l14 9-14 9V3z' />
                  </svg>
                </button>
              </div>
            </div>
            {testimonial.source && (
              <div className='absolute right-6 top-6'>
                <SourceIcon source={testimonial.source} />
              </div>
            )}
          </>
        )}
      </div>

      {testimonial.quote && (
        <p className='whitespace-pre-line px-[18px] pb-[42px] pt-4 text-left text-base leading-6 tracking-[-0.4px] sm:pb-[18px]'>
          {testimonial.quote}
        </p>
      )}
    </article>
  );
};

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) =>
  testimonial.type === 'video' ? (
    <VideoTestimonialCard testimonial={testimonial} />
  ) : (
    <TextTestimonialCard testimonial={testimonial} />
  );

const TestimonialWall = ({ showMoreLabel }: { showMoreLabel: string }) => {
  const wallRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [layout, setLayout] = useState<MasonryLayout | null>(null);
  const [visibleCount, setVisibleCount] = useState(
    Math.min(INITIAL_TESTIMONIAL_COUNT, TESTIMONIALS.length),
  );
  const visibleTestimonials = TESTIMONIALS.slice(0, visibleCount);
  const hasMoreTestimonials = visibleCount < TESTIMONIALS.length;

  useEffect(() => {
    const wall = wallRef.current;
    if (!wall) return;

    if (!('IntersectionObserver' in window)) {
      setIsNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        setIsNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: '600px 0px' },
    );

    observer.observe(wall);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const wall = wallRef.current;
    if (!wall || !isNearViewport) return;

    let animationFrame = 0;
    let measurementFrame = 0;

    const updateLayout = () => {
      cancelAnimationFrame(animationFrame);
      cancelAnimationFrame(measurementFrame);

      animationFrame = requestAnimationFrame(() => {
        const styles = window.getComputedStyle(wall);
        const contentWidth =
          wall.clientWidth -
          Number.parseFloat(styles.paddingLeft || '0') -
          Number.parseFloat(styles.paddingRight || '0');
        const columnCount = getColumnCount(contentWidth);
        const cardWidth =
          (contentWidth - MASONRY_GAP * (columnCount - 1)) / columnCount;

        itemRefs.current.forEach((item) => {
          if (item) item.style.width = `${cardWidth}px`;
        });

        measurementFrame = requestAnimationFrame(() => {
          const columnHeights = Array.from({ length: columnCount }, () => 0);
          const positions = itemRefs.current.map((item) => {
            const shortestColumn = columnHeights.indexOf(
              Math.min(...columnHeights),
            );
            const position = {
              width: cardWidth,
              x: shortestColumn * (cardWidth + MASONRY_GAP),
              y: columnHeights[shortestColumn],
            };

            columnHeights[shortestColumn] +=
              (item?.getBoundingClientRect().height || 0) + MASONRY_GAP;

            return position;
          });

          setLayout({
            height: Math.max(...columnHeights),
            positions,
          });
        });
      });
    };

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(wall);
    itemRefs.current.forEach((item) => {
      if (item) resizeObserver.observe(item);
    });
    updateLayout();

    document.fonts?.ready.then(updateLayout);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrame);
      cancelAnimationFrame(measurementFrame);
    };
  }, [isNearViewport, visibleCount]);

  return (
    <>
      <div
        className={hasMoreTestimonials ? 'relative -mb-16' : 'relative'}
        style={
          hasMoreTestimonials
            ? {
                WebkitMaskImage:
                  'linear-gradient(to bottom, black 0%, black calc(100% - 12rem), transparent 100%)',
                maskImage:
                  'linear-gradient(to bottom, black 0%, black calc(100% - 12rem), transparent 100%)',
              }
            : undefined
        }
      >
        <div
          ref={wallRef}
          className={
            layout
              ? 'relative w-full pr-[6px] transition-[height] duration-300 ease-out'
              : 'grid w-full items-start gap-6 pr-[6px] sm:grid-cols-2 lg:grid-cols-3'
          }
          style={layout ? { height: `${layout.height}px` } : undefined}
        >
          {visibleTestimonials.map((testimonial, index) => {
            const position = layout?.positions[index];

            return (
              <div
                key={testimonial.id}
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
                className={
                  position
                    ? 'absolute left-0 top-0 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]'
                    : undefined
                }
                style={
                  position
                    ? {
                        width: `${position.width}px`,
                        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
                      }
                    : undefined
                }
              >
                <TestimonialCard testimonial={testimonial} />
              </div>
            );
          })}
        </div>
      </div>

      {hasMoreTestimonials && (
        <div className='relative z-10 flex justify-center pb-3 sm:pb-4'>
          <button
            type='button'
            aria-expanded='false'
            onClick={() => {
              setLayout(null);
              setVisibleCount(TESTIMONIALS.length);
            }}
            className='inline-flex items-center gap-2 rounded-full border-2 border-black bg-white px-6 py-3 text-base font-semibold text-[#374151] shadow-[4px_4px_0_0_#7d56ed] transition duration-200 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#7d56ed] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#7d56ed]'
          >
            {showMoreLabel}
            <svg
              aria-hidden='true'
              viewBox='0 0 20 20'
              className='h-4 w-4 fill-current'
            >
              <path
                fillRule='evenodd'
                d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z'
                clipRule='evenodd'
              />
            </svg>
          </button>
        </div>
      )}
    </>
  );
};

const Testimonials = ({
  forceDark = false,
  hideHeading = false,
}: {
  forceDark?: boolean;
  /** For pages that introduce the wall with their own heading. */
  hideHeading?: boolean;
}) => {
  const { t } = useLocale('footer');

  return (
    <section className='body-font bg-transparent text-gray-600'>
      <div className='container mx-auto px-5'>
        {hideHeading ? null : (
          <div className='lg:text-center'>
            <p
              className={`mt-2 text-3xl font-extrabold leading-8 tracking-tight sm:text-4xl ${
                forceDark ? 'text-white' : 'text-gray-600 dark:text-white'
              }`}
            >
              {t('Testimonials')}
            </p>
            <p className='flex pt-3 lg:justify-center'>
              <a
                href='https://www.trustpilot.com/review/easy-peasy.ai'
                target='_blank'
                rel='noopener nofollow noreferrer'
              >
                <Image
                  src='/images/stars-5.svg'
                  alt='Our Trustpilot score'
                  width={200}
                  height={40}
                  loading='lazy'
                  decoding='async'
                />
              </a>
            </p>
            <p
              className={`mb-4 mt-4 max-w-2xl text-xl ${
                forceDark
                  ? 'text-slate-300'
                  : 'text-gray-500 dark:text-slate-300'
              } lg:mx-auto`}
            >
              {t(
                'Read the comments that people have made on public platforms.',
              )}
            </p>
          </div>
        )}
        <div className={hideHeading ? '-m-4' : '-m-4 mt-8'}>
          <TestimonialWall showMoreLabel={t('Show more...')} />
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
